"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useAppearance } from "@/components/site/AppearanceProvider";
import { MiniBoard } from "@/components/board/MiniBoard";
import { puzzlePosition, puzzleSide, type Puzzle } from "@/lib/puzzles";
import { useEngaged } from "@/lib/engagement";

export function PuzzlePreview({ puzzle, eager = false, themeOverride }: { puzzle: Puzzle; eager?: boolean; themeOverride?: "dark" | "light" }) {
  const container = useRef<HTMLDivElement>(null);
  const { appearance: savedAppearance } = useAppearance();
  const appearance = useMemo(() => themeOverride ? { ...savedAppearance, theme: themeOverride } : savedAppearance, [savedAppearance, themeOverride]);
  const [result, setResult] = useState<{ key: string; url: string | null } | null>(null);
  const fen = useMemo(() => puzzlePosition(puzzle).fen, [puzzle]);
  const key = `${fen}|${appearance.theme}|${appearance.board}|${appearance.pieces}|${appearance.style}`;
  const current = result?.key === key ? result : null;
  const engaged = useEngaged();
  useEffect(() => {
    if (!engaged) return;
    const controller = new AbortController();
    const load = () => {
      import("./preview-renderer")
        .then((module) => module.renderPuzzlePreview(fen, appearance, controller.signal))
        .then((url) => { if (!controller.signal.aborted) setResult({ key, url }); })
        .catch(() => { if (!controller.signal.aborted) setResult({ key, url: null }); });
    };
    if (eager || !("IntersectionObserver" in window)) load();
    else {
      const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { observer.disconnect(); load(); } }, { rootMargin: "250px" });
      if (container.current) observer.observe(container.current);
      return () => { controller.abort(); observer.disconnect(); };
    }
    return () => controller.abort();
  }, [fen, appearance, key, eager, engaged]);

  return <div ref={container} className="puzzle-preview" data-preview={current?.url ? "3d" : current ? "2d" : "loading"}>
    {/* eslint-disable-next-line @next/next/no-img-element -- a blob URL rendered in the browser; next/image has nothing to optimise */}
    {current?.url ? <img src={current.url} width={840} height={600} alt={`3D position for puzzle ${puzzle.id}, ${puzzleSide(puzzle)} to move`} draggable={false} />
      : current ? <div className="puzzle-preview-fallback"><MiniBoard fen={fen}/><span>2D preview · 3D unavailable</span></div>
      : <div className="puzzle-preview-loading" aria-label="Preparing 3D preview"><span className="puzzle-loading-symbol" aria-hidden>♞</span><span>Setting the board</span></div>}
  </div>;
}
